import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Calendar, Clock, Thermometer, MapPin, Heart } from 'lucide-react';
import { useRoleBasedApi } from '../../hooks/useRoleBasedApi';

interface Location {
  id: number;
  name: string;
  address?: string;
}

interface Dip {
  id: number;
  location: Location;
  dip_date: string;
  dip_time?: string;
  duration_minutes?: number;
  temperature?: number;
  notes?: string;
  created_at: string;
}

interface DipsListProps {
  showFavouritesOnly?: boolean;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatTime = (timeString: string): string => {
  return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const DipsList: React.FC<DipsListProps> = ({ showFavouritesOnly = false }) => {
  const [dips, setDips] = useState<Dip[]>([]);
  const [filteredDips, setFilteredDips] = useState<Dip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { get } = useRoleBasedApi();

  // Get favourite location IDs from localStorage
  const getFavouriteLocationIds = (): number[] => {
    try {
      const stored = localStorage.getItem('favourite_locations');
      return JSON.parse(stored || '[]');
    } catch {
      return [];
    }
  };

  useEffect(() => {
    fetchDips();
  }, []);

  // Filter dips when showFavouritesOnly changes or dips are loaded
  useEffect(() => {
    console.log('Filtering dips:', { showFavouritesOnly, dipsCount: dips.length });
    if (showFavouritesOnly) {
      const favouriteIds = getFavouriteLocationIds();
      console.log('Favourite location IDs:', favouriteIds);
      const filtered = dips.filter(dip => favouriteIds.includes(dip.location.id));
      console.log('Filtered dips count:', filtered.length);
      setFilteredDips(filtered);
    } else {
      setFilteredDips(dips);
    }
  }, [dips, showFavouritesOnly]);

  const fetchDips = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching dips, page:', page, 'showFavouritesOnly:', showFavouritesOnly);
      const data = await get(`/dips?page=${page}`);
      console.log('Dips API response:', data);
      setDips(data.dips);
      setHasMore(data.has_more || false);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch dips:', error);
      
      // Report specific backend issues
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          console.error('BACKEND ISSUE: Network error - backend may be down or CORS not configured');
        } else if (error.message.includes('401')) {
          console.error('BACKEND ISSUE: Authentication error - user session may be invalid');
        } else if (error.message.includes('404')) {
          console.error('BACKEND ISSUE: API endpoint not found - check if /dips endpoint exists');
        } else if (error.message.includes('500')) {
          console.error('BACKEND ISSUE: Server error - backend may have crashed');
        } else {
          console.error('BACKEND ISSUE:', error.message);
        }
      }
      
      setError('Failed to load dips');
    } finally {
      setLoading(false);
    }
  };

  // Defensive error message rendering
  const renderError = () => {
    if (!error) return null;
    if (
      error.toLowerCase().includes('auth') ||
      error.toLowerCase().includes('401') ||
      error.toLowerCase().includes('403') ||
      error.toLowerCase().includes('get user info')
    ) {
      return (
        <Card className="max-w-lg mx-auto my-8">
          <CardContent className="py-10 text-center">
            <div className="text-5xl mb-3">🔒</div>
            <h3 className="text-lg font-semibold mb-2">Please log in to view your dip history</h3>
            <p className="text-gray-600 mb-4">You need to be signed in to track and view your sauna sessions.</p>
            <Button asChild className="bg-sauna-orange hover:bg-sauna-orange/90 text-white font-semibold">
              <a href="/login">Log In</a>
            </Button>
          </CardContent>
        </Card>
      );
    }
    // Generic fallback
    return (
      <Card className="max-w-lg mx-auto my-8">
        <CardContent className="py-10 text-center">
          <div className="text-5xl mb-3">⚠️</div>
          <h3 className="text-lg font-semibold mb-2">Could not load dip history</h3>
          <p className="text-gray-600 mb-4">Something went wrong. Please try again later.</p>
        </CardContent>
      </Card>
    );
  };

  if (loading && dips.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading dips...</span>
      </div>
    );
  }

  if (error && dips.length === 0) {
    return renderError();
  }

  return (
    <div className='space-y-4'>
      {filteredDips.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <div className="text-4xl mb-4">
              {showFavouritesOnly ? '💖' : '🌊'}
            </div>
            <h3 className="text-lg font-semibold mb-2">
              {showFavouritesOnly ? 'No favourite dips yet' : 'No dips logged yet'}
            </h3>
            <p className="text-gray-600 mb-4">
              {showFavouritesOnly 
                ? 'Add locations to your favourites to see your dips from those places here.'
                : 'Start your sauna journey by logging your first dip!'
              }
            </p>
            {showFavouritesOnly ? (
              <Button asChild>
                <a href="/directory">Browse Locations</a>
              </Button>
            ) : (
              <Button>Log Your First Dip</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {filteredDips.map((dip) => (
            <DipCard key={dip.id} dip={dip} />
          ))}
          
          {hasMore && !showFavouritesOnly && (
            <div className="flex items-center justify-center space-x-2 mt-6">
              <Button
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => fetchDips(currentPage - 1)}
              >
                Previous
              </Button>
              
              <span className="text-sm text-gray-600">
                Page {currentPage} of {hasMore ? 'More' : 'Last'}
              </span>
              
              <Button
                variant="outline"
                disabled={!hasMore}
                onClick={() => fetchDips(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Individual Dip Card Component
const DipCard: React.FC<{ dip: Dip }> = ({ dip }) => {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-gray-500" />
              <h3 className="font-semibold text-lg">{dip.location.name}</h3>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(dip.dip_date)}</span>
              </div>
              
              {dip.dip_time && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>{formatTime(dip.dip_time)}</span>
                </div>
              )}
              
              {dip.duration_minutes && (
                <Badge variant="secondary">
                  {dip.duration_minutes}min
                </Badge>
              )}
              
              {dip.temperature && (
                <div className="flex items-center gap-1">
                  <Thermometer className="h-4 w-4" />
                  <span>{dip.temperature}°C</span>
                </div>
              )}
            </div>
            
            {dip.notes && (
              <p className="text-gray-700 mt-2 italic text-sm">
                "{dip.notes}"
              </p>
            )}
          </div>
          
          <div className="text-right ml-4">
            <div className="text-3xl">🌊</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DipsList; 